"use client";

import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import { useChatPromptSurfaceContext } from "./chat-prompt-surface-context";
import { getSpeechSeparator } from "./chat-speech-text";
import { useTextareaOverlayMetrics } from "./use-textarea-overlay-metrics";

/**
 * 音声入力の未確定テキストを、確定済み入力の直後に灰色イタリックの「仮入力」として
 * 重ねて表示するオーバーレイ。
 *
 * 仮入力表示中は呼び出し側（`ChatPromptSurfaceInput`）が実 textarea の文字を透明化し、
 * 本 component が「確定テキスト（通常色）＋仮テキスト（灰色イタリック）」を **同一の
 * テキストフロー**で描画する。確定テキストを textarea 側と二重描画してスペーサで
 * 位置合わせする方式だと、両者の折り返しの僅かな差で仮テキストの開始位置がズレるため、
 * 描画元を 1 つに統一して黒字と灰字が必ず連続するようにしている。
 *
 * 実 textarea の値は確定テキストのみを保持し、仮テキストは送信対象に含めない。
 * 選択・IME・キャレットは実 textarea に委ね、本 component は表示専用（pointer-events-none）。
 */
type ChatPromptSpeechInterimOverlayProps = {
  /** 実 textarea と同じ typography / padding を仮入力表示へ適用する。 */
  textClassName?: string;
};

export function ChatPromptSpeechInterimOverlay({
  textClassName,
}: ChatPromptSpeechInterimOverlayProps) {
  const { i18n } = useLingui();
  const { input, speechInterimTranscript, textareaElement, isExpanded } =
    useChatPromptSurfaceContext("ChatPromptSpeechInterimOverlay");
  const metrics = useTextareaOverlayMetrics(
    textareaElement,
    `${isExpanded}:${input.length}:${speechInterimTranscript.length}`,
  );

  if (!textareaElement || !speechInterimTranscript || metrics.height === 0) {
    return null;
  }

  const separator = getSpeechSeparator(
    input,
    speechInterimTranscript,
    i18n.locale,
  );

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 z-30 overflow-hidden"
      style={{
        top: `${metrics.top}px`,
        height: `${metrics.height}px`,
      }}
    >
      <div
        className={cn(
          "whitespace-pre-wrap break-words px-3 text-sm leading-5 text-foreground",
          isExpanded ? "py-3" : "py-2",
          textClassName,
        )}
        style={{
          transform: `translate(${-metrics.scrollLeft}px, ${-metrics.scrollTop}px)`,
        }}
      >
        {/* 確定テキストも本オーバーレイが可視で描画する（textarea 側は透明化される）。 */}
        <span>{`${input}${separator}`}</span>
        <span className="italic text-muted-foreground">
          {speechInterimTranscript}
        </span>
      </div>
    </div>
  );
}
